/**
 * The select of the automation settings: one value out of a few options,
 * shown like the table and workflow fields. Options may carry a description
 * (also the reason when an option is not available).
 */
import { Menu, MenuItem } from '@affine/component';
import type { ReactElement, SVGAttributes } from 'react';

import { DropdownChevron } from '../../shared/dropdown-chevron';
import { fieldLabel } from '../automation-panel.css';
import * as styles from './setting-select.css';

export interface SettingSelectOption<T extends string> {
  value: T;
  label: string;
  /** What the option does, or why it is not available. */
  description?: string;
  icon?: ReactElement<SVGAttributes<SVGElement>>;
  disabled?: boolean;
}

export interface SettingSelectProps<T extends string> {
  value: T | null;
  options: readonly SettingSelectOption<T>[];
  onChange: (value: T) => void;
  /** Shown while no option is selected. */
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
  'data-testid'?: string;
}

export function SettingSelect<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  'data-testid': testId,
}: SettingSelectProps<T>) {
  const selected = options.find(option => option.value === value);
  return (
    <Menu
      contentOptions={{ align: 'start', sideOffset: 4, className: styles.menu }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          prefixIcon={option.icon}
          selected={option.value === value}
          disabled={option.disabled}
          onSelect={() => onChange(option.value)}
        >
          <span className={styles.optionText}>
            <span>{option.label}</span>
            {option.description && (
              <span className={styles.optionDescription}>
                {option.description}
              </span>
            )}
          </span>
        </MenuItem>
      ))}
    >
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {selected?.icon && (
          <span className={styles.triggerIcon} aria-hidden="true">
            {selected.icon}
          </span>
        )}
        <span
          className={
            selected ? fieldLabel : `${fieldLabel} ${styles.placeholder}`
          }
        >
          {selected?.label ?? placeholder}
        </span>
        <DropdownChevron />
      </button>
    </Menu>
  );
}
